import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { CheckCircle, Download, Clock, Mail } from 'lucide-react';
import axios from 'axios';

const EcosystemSuccessPage = () => {
  const [paymentStatus, setPaymentStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const MAX_POLL_ATTEMPTS = 5;
  const POLL_INTERVAL = 2000; // 2 seconds

  useEffect(() => {
    // Get session_id from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setPaymentStatus('failed');
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/checkout-status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        console.log('Payment data received:', data);
        setPaymentStatus('success');
        setPaymentData(data);
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        // Still pending, continue polling
        setPollAttempts(attempt + 1);
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), POLL_INTERVAL);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempt < MAX_POLL_ATTEMPTS - 1) {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), POLL_INTERVAL);
      } else {
        setPaymentStatus('failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white py-16 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Checking Status */}
        {paymentStatus === 'checking' && (
          <Card className="p-12 text-center">
            <Clock className="w-20 h-20 text-green-600 mx-auto mb-6 animate-pulse" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Verifying Your Payment...</h2>
            <p className="text-lg text-gray-600 mb-6">Please wait while we confirm your purchase.</p>
            <div className="flex justify-center items-center space-x-2">
              <div className="w-3 h-3 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </Card>
        )}

        {/* Success Status */}
        {paymentStatus === 'success' && (
          <div className="space-y-8">
            <Card className="p-12 text-center bg-white border-4 border-green-600">
              <CheckCircle className="w-24 h-24 text-green-600 mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-black text-green-600 mb-4">
                🎉 Payment Successful!
              </h1>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Welcome to The Ultimate Author Ecosystem
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Thank you for your purchase! Your payment has been confirmed.
              </p>
            </Card>

            <Card className="p-8 md:p-12 bg-gradient-to-br from-green-600 to-green-700 text-white">
              <h3 className="text-3xl font-bold mb-8 text-center text-yellow-300">
                📥 Download Your Resources Now!
              </h3>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8">
                <h4 className="text-2xl font-bold mb-6 text-center">Your Complete eCoSystem Package:</h4>
                
                <div className="space-y-4 max-w-2xl mx-auto">
                  {/* Always show all downloads for ecosystem purchases */}
                  {(paymentData?.metadata?.package_id === 'complete_ecosystem' || 
                    paymentData?.metadata?.product_id === 'complete_ecosystem' ||
                    paymentData?.package_id === 'complete_ecosystem') ? (
                    <>
                      <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Download className="w-6 h-6 text-green-600" />
                          <span className="font-semibold">Facebook eCoSystem (100+ Platforms)</span>
                        </div>
                        <button
                          onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/facebook_ecosystem.pdf`, '_blank')}
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>

                      <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Download className="w-6 h-6 text-green-600" />
                          <span className="font-semibold">Instagram eCoSystem (100+ Accounts)</span>
                        </div>
                        <button
                          onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/instagram_ecosystem.pdf`, '_blank')}
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>

                      <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Download className="w-6 h-6 text-green-600" />
                          <span className="font-semibold">TikTok eCoSystem (100+ Creators)</span>
                        </div>
                        <button
                          onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/tiktok_ecosystem.pdf`, '_blank')}
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>

                      <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Download className="w-6 h-6 text-yellow-600" />
                          <span className="font-semibold">Author Resource Guide (Premium - Includes 100+ Websites!)</span>
                        </div>
                        <button
                          onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/author_resource_guide.pdf`, '_blank')}
                          className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-bold transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>

                      <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Download className="w-6 h-6 text-red-600" />
                          <span className="font-semibold">FREE BONUS: Case Study Blueprint</span>
                        </div>
                        <button
                          onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/case_study_blueprint.pdf`, '_blank')}
                          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Individual Product Downloads */}
                      {(paymentData?.metadata?.product_id === 'facebook_ecosystem' || paymentData?.package_id === 'facebook_ecosystem') && (
                        <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Download className="w-6 h-6 text-green-600" />
                            <span className="font-semibold">Facebook eCoSystem (100+ Platforms)</span>
                          </div>
                          <button
                            onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/facebook_ecosystem.pdf`, '_blank')}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                          >
                            Download PDF
                          </button>
                        </div>
                      )}
                      
                      {(paymentData?.metadata?.product_id === 'instagram_ecosystem' || paymentData?.package_id === 'instagram_ecosystem') && (
                        <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Download className="w-6 h-6 text-green-600" />
                            <span className="font-semibold">Instagram eCoSystem (100+ Accounts)</span>
                          </div>
                          <button
                            onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/instagram_ecosystem.pdf`, '_blank')}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                          >
                            Download PDF
                          </button>
                        </div>
                      )}
                      
                      {(paymentData?.metadata?.product_id === 'tiktok_ecosystem' || paymentData?.package_id === 'tiktok_ecosystem') && (
                        <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Download className="w-6 h-6 text-green-600" />
                            <span className="font-semibold">TikTok eCoSystem (100+ Creators)</span>
                          </div>
                          <button
                            onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/tiktok_ecosystem.pdf`, '_blank')}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                          >
                            Download PDF
                          </button>
                        </div>
                      )}
                      
                      {(paymentData?.metadata?.product_id === 'this_or_that_vol1' || paymentData?.package_id === 'this_or_that_vol1') && (
                        <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Download className="w-6 h-6 text-red-600" />
                            <span className="font-semibold">THIS OR THAT Vol. 1 - How Well Do You Know Them</span>
                          </div>
                          <button
                            onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/this_or_that_vol1.pdf`, '_blank')}
                            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                          >
                            Download PDF
                          </button>
                        </div>
                      )}
                      
                      {(paymentData?.metadata?.product_id === 'this_or_that_vol2' || paymentData?.package_id === 'this_or_that_vol2') && (
                        <div className="bg-white text-gray-900 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Download className="w-6 h-6 text-red-600" />
                            <span className="font-semibold">THIS OR THAT Vol. 2 - How Well Do You Know Your Partner</span>
                          </div>
                          <button
                            onClick={() => window.open(`${API_URL}/api/download-file/${new URLSearchParams(window.location.search).get('session_id')}/this_or_that_vol2.pdf`, '_blank')}
                            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                          >
                            Download PDF
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="bg-yellow-400 text-gray-900 rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold mb-3 flex items-center">
                  <Mail className="w-6 h-6 mr-2" />
                  📧 Check Your Email
                </h4>
                <p className="text-lg leading-relaxed">
                  We've also sent download links to your email. 
                  <br/><br/>
                  <strong>Save these files to your computer now!</strong> You can re-download them anytime using the links above.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h4 className="text-xl font-bold mb-3">🚀 What's Next?</h4>
                <ol className="space-y-3 text-lg">
                  <li>1. Download all your resources above</li>
                  <li>2. Start with the Author Resource Guide for overview</li>
                  <li>3. Select 20-30 platforms that match your genre</li>
                  <li>4. Follow the 90-day blueprint to build your audience</li>
                  <li>5. Join thousands of authors finding their readers!</li>
                </ol>
              </div>
            </Card>

            <div className="text-center space-y-4">
              <Button 
                onClick={() => window.location.href = '/'}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-12 py-6 text-xl rounded-lg"
              >
                Return to Homepage
              </Button>
              
              <p className="text-gray-600">
                Questions? Email us at{' '}
                <a href="mailto:info@eBlackBookListings.com" className="text-green-600 hover:text-green-700 font-semibold">
                  info@eBlackBookListings.com
                </a>
              </p>
            </div>
          </div>
        )}

        {/* Failed Status */}
        {(paymentStatus === 'failed' || paymentStatus === 'timeout') && (
          <Card className="p-12 text-center bg-white border-4 border-red-600">
            <div className="w-24 h-24 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-5xl">❌</span>
            </div>
            <h2 className="text-3xl font-bold text-red-600 mb-4">
              Payment Verification Issue
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              We're having trouble verifying your payment. This doesn't mean your payment failed.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 text-left">
              <h3 className="font-bold text-yellow-800 mb-2">What to do:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Check your email for a payment confirmation from Stripe</li>
                <li>Check your bank/card statement to see if the charge went through</li>
                <li>If charged, email us at eBlackBookListings@gmail.com with your order details</li>
                <li>If not charged, you can try purchasing again</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/author-ecosystem'}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                Return to Homepage
              </Button>
            </div>
          </Card>
        )}

        {/* Expired Status */}
        {paymentStatus === 'expired' && (
          <Card className="p-12 text-center bg-white border-4 border-orange-600">
            <Clock className="w-24 h-24 text-orange-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-orange-600 mb-4">
              Payment Session Expired
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Your payment session has expired. No charges were made to your account.
            </p>
            <Button 
              onClick={() => window.location.href = '/author-ecosystem'}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white font-bold px-12 py-6"
            >
              Try Again
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EcosystemSuccessPage;
